import { SETTINGS_NAV_ITEMS } from '@constants/Settings'
import { ISettings, ISettingsItem } from '@dataTypes/settings.type'
import {
  Dispatch,
  SetStateAction,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { hasStaffScope } from '@utils/staffScope.util'
import {
  getSettingsRouteById,
  getSettingsRouteByPathname,
  SCOPED_SETTINGS_ITEMS,
} from './settingsRoutes'

/** Drop scope-gated sub-items (and any section emptied as a result) the user can't access. */
const applyScopeVisibility = (sections: ISettings[]): ISettings[] =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        const required = SCOPED_SETTINGS_ITEMS[item.id]
        return (
          !required ||
          hasStaffScope(...(Array.isArray(required) ? required : [required]))
        )
      }),
    }))
    .filter((section) => {
      const original = SETTINGS_NAV_ITEMS.find((s) => s.id === section.id)
      return section.items.length > 0 || !original?.items.length
    })

interface SettingsHooks {
  activeItem: ISettingsItem | null
  expandedItems: string[]
  filteredNavItems: ISettings[]
  handleClearSearch: () => void
  handleSearch: (value: string) => void
  headerContextValue: {
    setHeaderButtons: Dispatch<SetStateAction<ReactNode | null>>
    setHeaderTitle: Dispatch<SetStateAction<ReactNode | null>>
    setShowSidebar: Dispatch<SetStateAction<boolean>>
  }
  headerButtons: ReactNode | null
  headerTitle: ReactNode | null
  onSubItemClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    item: ISettingsItem,
  ) => void
  searchQuery: string
  showSidebar: boolean
  toggleAllItems: (e: React.MouseEvent<HTMLButtonElement>) => void
  toggleItem: (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => void
}

const useSettings = (): SettingsHooks => {
  const navigate = useNavigate()
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [headerButtons, setHeaderButtons] = useState<ReactNode | null>(null)
  const [customHeaderTitle, setCustomHeaderTitle] = useState<ReactNode | null>(
    null,
  )
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showSidebar, setShowSidebar] = useState<boolean>(true)

  const headerContextValue = useMemo(
    () => ({
      setHeaderButtons,
      setHeaderTitle: setCustomHeaderTitle,
      setShowSidebar,
    }),
    [],
  )

  const activeItem = useMemo((): ISettingsItem | null => {
    const matchedRoute = getSettingsRouteByPathname(location.pathname)
    if (!matchedRoute) {
      return null
    }
    return {
      id: matchedRoute.id,
      name: matchedRoute.name,
      description: matchedRoute.description,
    }
  }, [location.pathname])

  const visibleNavItems = useMemo(
    () => applyScopeVisibility(SETTINGS_NAV_ITEMS),
    [],
  )

  const filteredNavItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return visibleNavItems
    }

    return visibleNavItems.filter((item) => {
      const matchesItem = item.name.toLowerCase().includes(normalizedQuery)

      const matchesSubItem = item.items.some((subItem) =>
        subItem.name.toLowerCase().includes(normalizedQuery),
      )

      return matchesItem || matchesSubItem
    })
  }, [searchQuery, visibleNavItems])

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) {
      return
    }

    const matchedParentIds = SETTINGS_NAV_ITEMS.filter((item) =>
      item.items.some((subItem) =>
        subItem.name.toLowerCase().includes(normalizedQuery),
      ),
    ).map((item) => item.id)

    if (matchedParentIds.length > 0) {
      setExpandedItems((prev) =>
        Array.from(new Set([...prev, ...matchedParentIds])),
      )
    }
  }, [searchQuery])

  useEffect(() => {
    const matchedRoute = getSettingsRouteByPathname(location.pathname)
    if (!matchedRoute || matchedRoute.parentId === matchedRoute.id) {
      return
    }

    setExpandedItems((prev) =>
      prev.length === 1 && prev[0] === matchedRoute.parentId
        ? prev
        : [matchedRoute.parentId],
    )
  }, [location.pathname])

  const handleClearSearch = (): void => {
    setSearchQuery('')
  }

  const handleSearch = (value: string): void => {
    setSearchQuery(value)
  }

  const toggleAllItems = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    if (expandedItems.length > 0) {
      setExpandedItems([])
    } else {
      setExpandedItems(SETTINGS_NAV_ITEMS.map((item) => item.id))
    }
  }
  const toggleItem = (
    e: React.MouseEvent<HTMLButtonElement>,
    itemId: string,
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    const navItem = SETTINGS_NAV_ITEMS.find((item) => item.id === itemId)
    if (navItem && navItem.items.length === 0) {
      const route = getSettingsRouteById(navItem.id)
      if (route) {
        void navigate(route.fullPath)
      }
      return
    }
    setExpandedItems((prev) =>
      prev.length === 1 && prev[0] === itemId ? [] : [itemId],
    )
  }

  const onSubItemClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: ISettingsItem,
  ): void => {
    e.preventDefault()
    e.stopPropagation()
    const route = getSettingsRouteById(item.id)
    if (route) {
      void navigate(route.fullPath)
    }
  }

  const headerTitle = customHeaderTitle ?? activeItem?.name ?? null

  return {
    activeItem,
    expandedItems,
    filteredNavItems,
    handleClearSearch,
    handleSearch,
    headerContextValue,
    headerButtons,
    headerTitle,
    onSubItemClick,
    searchQuery,
    showSidebar,
    toggleAllItems,
    toggleItem,
  }
}

export default useSettings
